import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../services/global-environment';
import { ApprovalStatusComponent } from '../../items/approval/approval-status/approval-status.component';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { ApprovalFlowDisplayComponent, ApprovalStep } from '../../shared/approval-flow-display/approval-flow-display.component';
import { UserService } from '../../items/approval/services/user.service';
import { AuthService } from '../../../services/auth.service';
import { DynamicFormComponent } from '../../dynamic-form/dynamic-form/dynamic-form.component';
import { FormDisplayComponent } from '../../form-display/form-display.component';

// 表單資料介面
interface ApprovalForm {
  id: string;
  companyid: string;
  componentname: string;
  formid: string;
  username?: string;
  assistantid?: string;
  assistantname?: string;
  createAt?: {
    type: string;
    value: number;
  };
  approval_flow_1?: {
    type: string;
    value: any; // 使用 any 型別來避免複雜的巢狀型別定義
  };
  createdAt?: string;
  updatedAt?: string;
  _ts?: number;
  [key: string]: any;
}

// API 回應介面
interface ApiResponse {
  success: boolean;
  message: string;
  requestId: string;
  data: {
    forms: ApprovalForm[];
    totalCount: number;
  };
}

@Component({
  selector: 'app-form-approval-list',
  standalone: true,
  imports: [CommonModule, FormsModule, ApprovalStatusComponent, TranslatePipe, ApprovalFlowDisplayComponent, DynamicFormComponent, FormDisplayComponent],
  templateUrl: './form-approval-list.component.html',
  styleUrl: './form-approval-list.component.scss'
})
export class FormApprovalListComponent implements OnInit {
  // 資料屬性
  forms: ApprovalForm[] = [];
  filteredForms: ApprovalForm[] = [];
  loading = false;
  error: string | null = null;

  // 搜尋與過濾
  searchKeyword = '';
  searchAssistant = '';
  searchUser = '';

  // 可用的選項
  availableAssistants: string[] = [];
  availableUsers: string[] = [];

  // 使用者ID
  currentUserId: string = '';

  // 表單查看相關屬性
  showViewForm = false;
  viewFormConfig: any[] = [];
  viewFormData: any = {};
  viewFormName: string = '';

  // 環境設定
  private apiUrl = environment.apiUrl;
  private companyid = environment.companyid;

  constructor(private http: HttpClient, private userService: UserService, private authService: AuthService) {}

  ngOnInit(): void {
    this.currentUserId = this.authService.getUserInfo().userid;
    this.loadApprovalForms();
  }

  /**
   * 載入有簽核流程的表單
   */
  loadApprovalForms(): void {
    this.loading = true;
    this.error = null;

    const url = `${this.apiUrl}/api/form-manager/get_approval_forms`;
    const params = { companyid: this.companyid };

    this.http.get<ApiResponse>(url, { params }).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.forms = response.data.forms;
          this.filteredForms = [...this.forms];
          this.extractFilterOptions();
          console.log('成功載入簽核表單列表:', response.data);
        } else {
          this.error = response.message || '載入失敗';
          console.warn('API 回應異常:', response);
        }
        this.loading = false;
      },
      error: (error) => {
        this.error = '載入簽核表單列表時發生錯誤';
        console.error('載入簽核表單列表錯誤:', error);
        this.loading = false;
      }
    });
  }

  /**
   * 提取篩選選項
   */
  extractFilterOptions(): void {
    // 提取助手名稱
    const assistantSet = new Set<string>();
    const userSet = new Set<string>();

    this.forms.forEach(form => {
      if (form.assistantname) {
        assistantSet.add(form.assistantname);
      }
      if (form.username) {
        userSet.add(form.username);
      }
    });

    this.availableAssistants = Array.from(assistantSet).sort();
    this.availableUsers = Array.from(userSet).sort();
  }

  /**
   * 執行搜尋與過濾
   */
  searchForms(): void {
    this.filteredForms = this.forms.filter(form => {
      // 關鍵字搜尋（表單名稱）
      const matchKeyword = !this.searchKeyword ||
        form.componentname?.toLowerCase().includes(this.searchKeyword.toLowerCase());

      // 助手篩選
      const matchAssistant = !this.searchAssistant ||
        form.assistantname === this.searchAssistant;

      // 使用者篩選
      const matchUser = !this.searchUser ||
        form.username === this.searchUser;

      return matchKeyword && matchAssistant && matchUser;
    });
  }

  /**
   * 清除搜尋條件
   */
  clearSearch(): void {
    this.searchKeyword = '';
    this.searchAssistant = '';
    this.searchUser = '';
    this.filteredForms = [...this.forms];
  }

  /**
   * 查看表單詳情
   */
  async viewForm(form: ApprovalForm): Promise<void> {
    console.log('查看表單:', form);

    try {
      // 設定基本資訊
      this.viewFormName = form.componentname;

      // 檢查必要資料
      const formId = form.formid;
      if (!formId) {
        throw new Error('表單缺少必要的 formid 或 id');
      }

      // 取得表單配置
      await this.loadFormConfig(formId);

      // 直接使用傳入的 form 物件作為表單資料
      this.viewFormData = form;

      // 顯示查看視窗
      this.showViewForm = true;

    } catch (error) {
      console.error('載入表單詳情失敗:', error);
      alert('載入表單詳情失敗，請稍後再試。');
    }
  }

  /**
   * 載入表單配置
   */
  private async loadFormConfig(formId: string): Promise<void> {
    const url = `${this.apiUrl}/api/form-manager/get_specific_form_config`;
    const params = {
      companyid: this.companyid,
      formid: formId
    };

    const response = await this.http.get<any>(url, { params }).toPromise();
    if (response?.success && response?.data?.config) {
      this.viewFormConfig = response.data.config;
    } else {
      throw new Error('無法取得表單配置');
    }
  }

  /**
   * 載入表單資料
   */
  private async loadFormData(formId: string): Promise<void> {
    const url = `${this.apiUrl}/api/form-manager/get_form_data`;
    const params = {
      formid: formId,
      companyid: this.companyid
    };

    const response = await this.http.get<any>(url, { params }).toPromise();
    if (response?.success && response?.data) {
      this.viewFormData = response.data;
    } else {
      throw new Error('無法取得表單資料');
    }
  }

  /**
   * 關閉表單查看視窗
   */
  closeViewForm(): void {
    this.showViewForm = false;
    this.viewFormConfig = [];
    this.viewFormData = {};
    this.viewFormName = '';
  }

  /**
   * 簽核完成回調
   */
  onApprovalComplete(approvalInstance: any): void {
    console.log('簽核完成:', approvalInstance);

    // 重新載入列表以更新狀態
    this.loadApprovalForms();
  }

  /**
   * 取得簽核流程資訊
   */
  getApprovalFlowInfo(form: ApprovalForm): string {
    if (form.approval_flow_1?.value) {
      try {
        let flowInfo: any;
        let steps: any[] = [];

        // 適應新的巢狀資料結構，使用 any 型別避免型別檢查問題
        const approvalData: any = form.approval_flow_1.value;

        if (approvalData.value?.steps) {
          // 雙層巢狀結構
          flowInfo = approvalData.value;
          steps = flowInfo.steps;
        } else if (approvalData.steps) {
          // 單層結構
          flowInfo = approvalData;
          steps = flowInfo.steps;
        } else if (Array.isArray(approvalData)) {
          // 直接是陣列
          steps = approvalData;
        }

        if (steps && steps.length > 0) {
          const flowName = flowInfo?.flowName || '';
          if (flowName) {
            return `${flowName} (${steps.length} 步驟)`;
          } else {
            return `${steps.length} 步驟簽核流程`;
          }
        }
      } catch (error) {
        console.warn('解析簽核流程資訊失敗:', error);
      }
    }
    return '簽核流程';
  }

  /**
   * 格式化日期
   */
  formatDate(dateInput: string | number | undefined): string {
    if (!dateInput) return '-';
    try {
      let date: Date;

      if (typeof dateInput === 'number') {
        // 如果是數字，判斷是否為 Unix 時間戳（秒）
        // Unix 時間戳如果小於 10^12，通常是以秒為單位
        if (dateInput < 10000000000) {
          // 轉換為毫秒
          date = new Date(dateInput * 1000);
        } else {
          // 已經是毫秒
          date = new Date(dateInput);
        }
      } else {
        // 字串格式
        const numericValue = parseInt(dateInput.toString());
        if (!isNaN(numericValue) && numericValue < 10000000000) {
          // 數字字串，且為秒級時間戳
          date = new Date(numericValue * 1000);
        } else {
          // 一般日期字串
          date = new Date(dateInput);
        }
      }

      return date.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateInput?.toString() || '-';
    }
  }

  /**
   * 追蹤函數，用於 ngFor 效能優化
   */
  trackByFormId(index: number, form: ApprovalForm): string {
    return form.id || form.formid || index.toString();
  }

  /**
   * 取得簽核步驟資料
   */
  getApprovalSteps(form: ApprovalForm): any[] {
    if (form.approval_flow_1?.value) {
      try {
        // 適應新的巢狀資料結構，使用 any 型別
        const approvalData: any = form.approval_flow_1.value;

        if (approvalData.value?.steps) {
          // 雙層巢狀結構
          return approvalData.value.steps;
        } else if (approvalData.steps) {
          // 單層結構
          return approvalData.steps;
        } else if (Array.isArray(approvalData)) {
          // 直接是陣列
          return approvalData;
        }
      } catch (error) {
        console.warn('解析簽核步驟失敗:', error);
      }
    }
    return [];
  }

  /**
   * 取得步驟狀態樣式類別
   */
  getStepStatusClass(step: any, index: number): string {
    // 檢查步驟是否有狀態資訊
    if (step.status) {
      switch (step.status) {
        case 'approved':
          return 'step-approved';
        case 'rejected':
          return 'step-rejected';
        case 'in_progress':
          return 'step-in-progress';
        case 'pending':
        default:
          return 'step-pending';
      }
    }

    // 如果沒有狀態資訊，使用預設邏輯
    // 第一步設為進行中，其他為等待中
    if (index === 0) {
      return 'step-in-progress';
    }
    return 'step-pending';
  }

  /**
   * 取得步驟狀態文字
   */
  getStepStatusText(step: any, index: number): string {
    // 檢查步驟是否有狀態資訊
    if (step.status) {
      switch (step.status) {
        case 'approved':
          return '已核准';
        case 'rejected':
          return '已拒絕';
        case 'in_progress':
          return '進行中';
        case 'pending':
        default:
          return '等待中';
      }
    }

    // 如果沒有狀態資訊，使用預設邏輯
    if (index === 0) {
      return '進行中';
    }
    return '等待中';
  }

  /**
   * 取得指定步驟的可簽核人員列表
   */
  getStepApproverList(step: any): any[] {
    return step.approverList || [];
  }

  /**
   * 取得指定步驟的可簽核人員名稱（字串格式）
   */
  getStepApproverNames(step: any): string {
    const approvers = this.getStepApproverList(step);
    if (approvers.length === 0) {
      return '無指定人員';
    }
    return approvers.map(approver => approver.name || approver.id).join('、');
  }

  /**
   * 將原始步驟資料轉換為 ApprovalFlowDisplayComponent 需要的格式
   */
  convertToApprovalFlowSteps(form: ApprovalForm): ApprovalStep[] {
    const rawSteps = this.getApprovalSteps(form);

    return rawSteps.map((step, index) => {
      // 取得簽核者名稱
      const approverNames = this.getStepApproverList(step)
        .map(approver => approver.name || approver.id)
        .filter(name => name && name.trim() !== '');

      // 取得簽核者ID列表
      const approverIds = this.getStepApproverList(step)
        .map(approver => approver.id)
        .filter(id => id && id.trim() !== '');

      // 決定步驟狀態 - 根據圖片中的顏色規則
      let status: 'pending' | 'in_progress' | 'approved' | 'rejected' = 'pending';

      if (step.status) {
        // 如果有明確的狀態，使用它
        status = step.status;
      } else {
        // 預設邏輯：第一步設為進行中（黃色），其他為等待中（灰色）
        if (index === 0) {
          status = 'in_progress';
        } else {
          status = 'pending';
        }
      }

      // 判斷是否可點擊：當前使用者是簽核人且步驟狀態為 pending 或 in_progress
      const clickable = (status === 'pending' || status === 'in_progress') &&
                       approverIds.includes(this.currentUserId);

      return {
        stepName: step.stepName || `步驟 ${index + 1}`,
        approverNames: approverNames.length > 0 ? approverNames : undefined,
        approverName: approverNames.length > 0 ? approverNames.join(', ') : '無指定人員',
        status: status,
        comment: step.comment,
        actionDate: step.actionDate,
        clickable: clickable,
        stepIndex: index,
        approverIds: approverIds
      };
    });
  }

  /**
   * 取得步驟的部門角色資訊
   */
  getStepDepartmentRoleInfo(step: any): string {
    if (step.approverType === 'role_based') {
      const deptName = step.departmentName || '未知部門';
      const roleName = step.roleName || '未知角色';
      return `${deptName} - ${roleName}`;
    } else if (step.approverType === 'specific_user') {
      return '指定人員';
    }
    return '未定義';
  }

  /**
   * 檢查步驟是否為動態部門
   */
  isStepDynamicDepartment(step: any): boolean {
    return step.isDynamicDepartment === true;
  }

  /**
   * 取得步驟的完整描述
   */
  getStepDescription(step: any): string {
    const approverInfo = this.getStepDepartmentRoleInfo(step);
    const approverNames = this.getStepApproverNames(step);
    const dynamic = this.isStepDynamicDepartment(step) ? ' (動態)' : '';

    return `${approverInfo}${dynamic} - ${approverNames}`;
  }

  /**
   * 取得簽核流程的完整名稱
   */
  getFlowName(form: ApprovalForm): string {
    if (form.approval_flow_1?.value) {
      try {
        // 適應新的巢狀資料結構，使用 any 型別
        const approvalData: any = form.approval_flow_1.value;

        if (approvalData.value?.flowName) {
          return approvalData.value.flowName;
        } else if (approvalData.flowName) {
          return approvalData.flowName;
        }
      } catch (error) {
        console.warn('取得流程名稱失敗:', error);
      }
    }
    return form.componentname || '未命名流程';
  }

  /**
   * 取得簽核流程的描述
   */
  getFlowDescription(form: ApprovalForm): string {
    if (form.approval_flow_1?.value) {
      try {
        // 適應新的巢狀資料結構，使用 any 型別
        const approvalData: any = form.approval_flow_1.value;

        if (approvalData.value?.flowDescription) {
          return approvalData.value.flowDescription;
        } else if (approvalData.flowDescription) {
          return approvalData.flowDescription;
        }
      } catch (error) {
        console.warn('取得流程描述失敗:', error);
      }
    }
    return '';
  }

  /**
   * 刪除表單記錄
   */
  deleteForm(form: ApprovalForm): void {
    // 顯示確認對話框，包含更詳細的資訊
    const formInfo = `表單名稱：${form.componentname}`;
    const creatorInfo = form.username ? `建立者：${form.username}` : '';
    const timeInfo = form._ts ? `建立時間：${this.formatDate(form._ts)}` : '';

    const confirmMessage = [
      '確定要刪除以下表單嗎？',
      '',
      formInfo,
      creatorInfo,
      timeInfo,
      '',
      '⚠️ 此操作無法復原，表單將被標記為已刪除。'
    ].filter(line => line !== undefined && line !== '').join('\n');

    if (!confirm(confirmMessage)) {
      return;
    }

    console.log('準備刪除表單:', form);

    const url = `${this.apiUrl}/api/form-manager/delete_record`;
    const requestData = {
      companyid: form.companyid,
      id: form.id
    };

    console.log('發送刪除請求:', requestData);

    this.http.post<any>(url, requestData).subscribe({
      next: (response) => {
        console.log('刪除回應:', response);
        if (response.success) {
          // 從列表中移除已刪除的項目
          this.forms = this.forms.filter(f => f.id !== form.id);
          this.filteredForms = this.filteredForms.filter(f => f.id !== form.id);

          // 重新提取篩選選項
          this.extractFilterOptions();

          const successMessage = `✅ 表單「${form.componentname}」已成功刪除`;
          alert(successMessage);
          console.log('表單刪除成功:', response.data);
        } else {
          const errorMessage = `❌ 刪除失敗：${response.message || '未知錯誤'}`;
          alert(errorMessage);
          console.error('刪除失敗:', response);
        }
      },
      error: (error) => {
        console.error('刪除表單時發生錯誤:', error);
        const errorMessage = `❌ 刪除失敗：${error.error?.message || error.message || '網路連接錯誤，請稍後再試'}`;
        alert(errorMessage);
      }
    });
  }

}


